import React, { useEffect, useState } from 'react';
import Editor, { useMonaco } from '@monaco-editor/react';
// enable only when there issue loading the original file
// loader.config({
//   paths: {
//     vs: `https://fastly.jsdelivr.net/npm/monaco-editor@0.48.0/min/vs`,
//   },
// });

export interface MonacoEditorProps {
  code: string;
  onCodeChange: (string) => void;
  lang: 'html' | 'css' | 'javascript' | 'bash' | `json`;
  readOnly: boolean;
  autoFormat?: boolean;
  setJsErrors?: (errors: any[]) => void;
  monacoOptions?: any;
}

export function MonacoEditor({
  code,
  onCodeChange,
  lang,
  readOnly,
  autoFormat,
  setJsErrors,
  monacoOptions,
}: MonacoEditorProps) {
  const monaco = useMonaco();
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    if (monaco) {
      monaco.editor.defineTheme(`loomi`, {
        base: `vs`,
        inherit: true,
        rules: [],
        colors: {
          'editor.background': `#F6F8F9`,
        },
      });
      setIsLoaded(true);
    }
  }, [monaco]);

  const handleMount = (editor) => {
    editor.updateOptions({ readOnly });
    if (autoFormat) {
      editor.getAction(`editor.action.formatDocument`).run();
    }
  };

  const onValidate = (value) => {
    setJsErrors?.(value);
    if (autoFormat) {
      monaco.editor.getAction(`editor.action.formatDocument`).run();
    }
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isLoaded && (
        <Editor
          height="100%"
          width="100%"
          defaultLanguage={lang}
          language={lang}
          defaultValue={code}
          value={code}
          onChange={onCodeChange}
          theme="loomi"
          options={{
            ...defaultMonacoOptions,
            ...(monacoOptions || {}),
            readOnly,
          }}
          onMount={handleMount}
          onValidate={onValidate}
        />
      )}
    </>
  );
}

export const HtmlCodeEditor = ({
  code,
  onChange,
  readOnly,
}: {
  code: string;
  onChange: (code: string) => void;
  readOnly: boolean;
}) => (
  <MonacoEditor
    code={code}
    onCodeChange={onChange}
    lang="html"
    readOnly={readOnly}
  />
);

export const CssCodeEditor = ({
  code,
  onChange,
  readOnly,
}: {
  code: string;
  onChange: (code: string) => void;
  readOnly: boolean;
}) => (
  <MonacoEditor
    code={code}
    onCodeChange={onChange}
    lang="css"
    readOnly={readOnly}
  />
);

export const JsCodeEditor = ({
  code,
  onChange,
  readOnly,
  setJsErrors,
}: {
  code: string;
  onChange: (code: string) => void;
  readOnly: boolean;
  setJsErrors?: (errors: any[]) => void;
}) => (
  <MonacoEditor
    setJsErrors={setJsErrors}
    code={code}
    onCodeChange={onChange}
    lang="javascript"
    readOnly={readOnly}
  />
);

const defaultMonacoOptions = {
  minimap: {
    enabled: false,
  },
  fontSize: 18,
  fontFamily: `JetBrains Mono`,
};
